import { useEffect, useRef } from 'react';

/**
 * Polling that stops while nobody is looking.
 *
 * Every `setInterval` poll in this app used to run for as long as the tab
 * existed, visible or not. An abandoned background tab kept hitting the API
 * forever — which spends Moralis/Alchemy credits and, before the backend
 * caching work, woke the Neon compute on a loop. See
 * NEON-SCALE-TO-ZERO-PHASE0.md §3.
 *
 * `document.visibilityState` is the right signal rather than `blur`/`focus`:
 * it covers backgrounded tabs, minimised windows and locked screens, and it
 * does not fire when the user merely clicks another window while still
 * watching this one.
 */

/** True when the page is being displayed. Defaults to true if there is no DOM. */
export function isTabVisible(): boolean {
  if (typeof document === 'undefined') return true;
  return document.visibilityState !== 'hidden';
}

/**
 * Call `callback` every `intervalMs`, but only while the tab is visible.
 *
 * On becoming visible the callback fires immediately — a tab that was hidden
 * for an hour should not show hour-old data while it waits for the next tick —
 * and the interval restarts from that moment.
 *
 * `enabled: false` stops polling entirely; use it for authenticated polls so a
 * logged-out visitor makes no calls at all. The callback is read from a ref, so
 * it does not need to be memoised and changing it will not restart the timer.
 *
 * Does NOT perform an initial call on mount — callers usually need to fetch on
 * mount for their own reasons (and often with different arguments), so that
 * stays their decision.
 */
export function useVisiblePoll(
  callback: () => void,
  intervalMs: number,
  enabled = true,
): void {
  const latest = useRef(callback);

  useEffect(() => {
    latest.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled) return;

    let timer: ReturnType<typeof setInterval> | undefined;

    const start = () => {
      if (timer !== undefined) return;
      timer = setInterval(() => latest.current(), intervalMs);
    };

    const stop = () => {
      if (timer === undefined) return;
      clearInterval(timer);
      timer = undefined;
    };

    const onVisibilityChange = () => {
      if (isTabVisible()) {
        latest.current();
        start();
      } else {
        stop();
      }
    };

    if (isTabVisible()) start();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [intervalMs, enabled]);
}
